/**
 * Attachment type validation utilities.
 *
 * Shared between AttachmentsModal and ChatModals so that the validation is applied
 * both at the UI level (modal) and at the save level (before messageFiles is updated).
 */

// Known extensions for each MIME type — used when the browser reports an empty file.type.
export const ATTACHMENT_TYPE_EXTENSIONS = {
  "application/pdf": [".pdf"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel": [".xls"],
  "text/x-tex": [".tex"],
  "application/x-tex": [".tex"],
  "application/x-latex": [".latex"],
  "text/plain": [".txt"],
  "text/markdown": [".md"],
  "text/x-markdown": [".md"],
};

/**
 * Returns the content types accepted by the assistant.
 * Empty array when no attachment_config is set (safe default: nothing accepted).
 *
 * @param {object|null|undefined} attachmentConfig - The assistant's attachment_config.
 * @returns {string[]} List of accepted MIME types.
 */
export function getAcceptedTypes(attachmentConfig) {
  if (
    attachmentConfig &&
    typeof attachmentConfig === "object" &&
    Object.keys(attachmentConfig).length > 0
  ) {
    return Object.entries(attachmentConfig)
      .filter(([, cfg]) => cfg && cfg.accepted)
      .map(([type]) => type);
  }
  return [];
}

/**
 * Returns the `accept` attribute value for a file input derived from the accepted types.
 *
 * @param {string[]} acceptedTypes
 * @returns {string}
 */
export function buildAcceptAttr(acceptedTypes) {
  return [
    ...acceptedTypes,
    ...acceptedTypes.flatMap((type) => ATTACHMENT_TYPE_EXTENSIONS[type] || []),
  ].join(",");
}

/**
 * Returns true if the file's type or extension is in the accepted types list.
 *
 * @param {File} file
 * @param {string[]} acceptedTypes
 * @returns {boolean}
 */
export function isFileTypeAccepted(file, acceptedTypes) {
  if (!acceptedTypes || acceptedTypes.length === 0) return false;
  // Fast path: MIME type match.
  if (file?.type && acceptedTypes.includes(file.type)) return true;
  // Fallback: extension match (some browsers report empty MIME type).
  const name = (file?.name || "").toLowerCase();
  return acceptedTypes.some((type) =>
    (ATTACHMENT_TYPE_EXTENSIONS[type] || []).some((ext) => name.endsWith(ext))
  );
}
